/**
 * Загрузка фото к заявке (canon design-guest §16, common-поле всех форм).
 *
 * Поток: POST /v1/files/upload (multipart, kind=photo) → API стримит файл в
 * MinIO по loopback → возвращает объектный `key`, который уходит в заявку /
 * cover_url события. Серверный прокси (НЕ presigned-PUT): в одно-хостовом
 * проде MinIO слушает 127.0.0.1:9002 без публичного ingress, presigned-URL
 * из браузера недоступны (фото «не сохранялись»). Endpoint живой.
 */
import { useRef, useState } from 'react';
import { uploadFile } from '@hsc/api-client';

export interface PhotoSlot {
  /** Локальный id слота (для key/удаления), не серверный. */
  id: string;
  name: string;
  status: 'uploading' | 'done' | 'error';
  /** Объектный ключ в MinIO — есть только при status=done. */
  key?: string;
  /** ObjectURL для превью ('' если среда не умеет createObjectURL). */
  previewUrl: string;
}

/** Превью-URL без падения в средах без createObjectURL (jsdom). */
function safeObjectUrl(file: File): string {
  try {
    return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
  } catch {
    return '';
  }
}

function safeRevoke(url: string): void {
  try {
    if (url !== '' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  } catch {
    /* ignore */
  }
}

export interface UsePhotoUpload {
  photos: readonly PhotoSlot[];
  /** Добавить файлы (не больше max суммарно; лишние молча отбрасываются). */
  addFiles: (files: FileList | null) => void;
  removePhoto: (id: string) => void;
  /** true пока хотя бы один файл ещё грузится. */
  isUploading: boolean;
  /** Объектные ключи успешно загруженных фото — для body заявки. */
  uploadedKeys: readonly string[];
}

export function usePhotoUpload(max = 3): UsePhotoUpload {
  const [photos, setPhotos] = useState<readonly PhotoSlot[]>([]);
  const seq = useRef(0);

  const patchSlot = (id: string, patch: Partial<PhotoSlot>): void => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const addFiles = (files: FileList | null): void => {
    if (!files) return;
    const room = max - photos.length;
    const accepted = Array.from(files).slice(0, Math.max(0, room));
    for (const file of accepted) {
      seq.current += 1;
      const id = `photo-${seq.current}`;
      setPhotos((prev) => [
        ...prev,
        { id, name: file.name, status: 'uploading', previewUrl: safeObjectUrl(file) },
      ]);
      void (async () => {
        try {
          const res = await uploadFile(file, 'photo');
          patchSlot(id, { status: 'done', key: res.key });
        } catch {
          patchSlot(id, { status: 'error' });
        }
      })();
    }
  };

  const removePhoto = (id: string): void => {
    setPhotos((prev) => {
      const victim = prev.find((p) => p.id === id);
      if (victim) safeRevoke(victim.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  return {
    photos,
    addFiles,
    removePhoto,
    isUploading: photos.some((p) => p.status === 'uploading'),
    uploadedKeys: photos.flatMap((p) =>
      p.status === 'done' && p.key !== undefined ? [p.key] : [],
    ),
  };
}
